import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { enqueueSnackbar } from "notistack";
import Select from "react-select";
import Switch from "react-switch";
import { useNavigate } from "react-router-dom";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  BARCODE_CATEGORY_OPTIONS,
  BARCODE_REVIEW_SCOPE_OPTIONS,
  checkAssetBarcodeExists,
  downloadBarcodeProtectedResource,
  fetchBarcodeBlob,
  generateBarcodes,
  getAssetByBarcode,
  getBarcodeExportUrl,
  getBarcodePdfGroups,
  getBarcodeQueryScanUrl,
  getBarcodeScanUrl,
  getBarcodeShortUrl,
  getNamingInstitutionOptions,
  getNamingConfigSummary,
  openBarcodeProtectedResource,
  previewBarcodeGenerationCount,
  resolveBarcodeUrl,
} from "../../services/BarcodeService";
import { getToken } from "../../services/AuthToken";
import { getAllAstInstitutions, getAllWards } from "../../services/ValitanApiService";
import "./BarcodeDashboard.css";

const DEFAULT_FORM = {
  institutionId: "",
  institutionCode: "",
  institutionName: "",
  institutionSelectorType: "",
  wardId: "",
  wardIds: [],
  namingConfigId: "",
  useNamingConfig: false,
  assetCategoryCode: "VEH",
  reviewScope: "reviewed",
  mode: "new",
  textOnly: false,
};

const parseApiPayload = (response) => response?.data?.data ?? response?.data ?? null;

const normalizeGenerationPreview = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  return {
    assetCategoryCode: payload.assetCategoryCode ?? payload.asset_category_code ?? null,
    institutionCode: payload.institutionCode ?? payload.institution_code ?? null,
    reviewScope: payload.reviewScope ?? payload.review_scope ?? null,
    mode: payload.mode ?? null,
    matchingAssets: payload.matchingAssets ?? payload.matching_assets ?? 0,
    totalRequiredLabels: payload.totalRequiredLabels ?? payload.total_required_labels ?? 0,
    existingLabels: payload.existingLabels ?? payload.existing_labels ?? 0,
    barcodesToGenerate: payload.barcodesToGenerate ?? payload.barcodes_to_generate ?? 0,
    matchedInstitutions: payload.matchedInstitutions ?? payload.matched_institutions ?? [],
    hasExistingBarcodes: payload.hasExistingBarcodes ?? payload.has_existing_barcodes ?? false,
    buildingCount: payload.buildingCount ?? payload.building_count ?? 0,
    siteWorksCount: payload.siteWorksCount ?? payload.site_works_count ?? 0,
  };
};

const parseListPayload = (response) => {
  if (Array.isArray(response)) return response;
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const getInstitutionOptionKey = (institution, fallbackIndex = "") => {
  const id = String(institution?.id || institution?.institution_id || institution?.institutionId || "").trim();
  const name = String(institution?.name || institution?.institution_name || institution?.institutionName || "").trim();
  const wardId = String(institution?.ward_id || institution?.wardId || "").trim();
  const code = String(institution?.code || institution?.institution_code || institution?.institutionCode || "").trim();
  return id || [name, wardId, code, fallbackIndex].filter(Boolean).join("|");
};

const getNamingConfigInstitutionFilters = (config) => {
  const rawFilters = Array.isArray(config?.institution_filters)
    ? config.institution_filters
    : Array.isArray(config?.institutionFilters)
      ? config.institutionFilters
      : [];

  if (rawFilters.length > 0) {
    return rawFilters
      .map((filter) => ({
        name: String(filter?.name || "").trim(),
        wardId: String(filter?.ward_id || filter?.wardId || "").trim(),
      }))
      .filter((filter) => filter.name);
  }

  const fallbackName = String(config?.institution_name || config?.institutionName || "").trim();
  const fallbackWardId = String(config?.ward_id || config?.wardId || "").trim();
  if (!fallbackName) return [];
  return [{ name: fallbackName, wardId: fallbackWardId }];
};

const formatNamingConfigInstitutionLabel = (config) => {
  const filters = getNamingConfigInstitutionFilters(config);
  if (filters.length === 0) {
    return String(config?.institution_name || config?.institutionName || "").trim();
  }
  return filters.map((filter) => filter.name).join(", ");
};

const normalizeReviewScopeForApi = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized || null;
};

const normalizeReviewedForApi = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "reviewed") return true;
  if (normalized === "unreviewed") return false;
  return null;
};

const getPipelineErrorMessage = (error, fallbackMessage) => {
  const backendMessage = error?.response?.data?.message;
  const output = error?.response?.data?.data?.output || "";

  if (output.includes("psql: command not found")) {
    return "Barcode generation ran, but token sync failed because PostgreSQL client (psql) is missing on backend.";
  }

  if (output.includes("python3 is required")) {
    return "Barcode data generation completed, but PDF/verification failed because python3 is missing on backend.";
  }

  if (output.includes("Python Pillow (PIL) is required")) {
    return "Barcode data generation completed, but PDF/verification failed because Python Pillow is missing on backend.";
  }

  if (output.includes("reportlab")) {
    return "Barcode data generation completed, but PDF/verification failed because Python reportlab is missing on backend.";
  }

  return backendMessage || error?.message || fallbackMessage;
};

const buildGeneratePayload = (formValues) => {
  const fullRebuild = formValues.mode === "all";
  const reviewScope = normalizeReviewScopeForApi(formValues.reviewScope);
  const reviewed = normalizeReviewedForApi(formValues.reviewScope);
  const institutionName = String(formValues.institutionName || "").trim();
  const institutionId = String(formValues.institutionId || "").trim();
  const wardId = String(formValues.wardId || "").trim();
  const assetCategoryCode = String(formValues.assetCategoryCode || "").trim().toUpperCase();
  const namingConfigId = formValues.useNamingConfig && formValues.namingConfigId
    ? formValues.namingConfigId
    : null;
  const useDirectInstitution = !namingConfigId && wardId && (institutionId || institutionName);
  const directInstitutionName = useDirectInstitution ? (institutionName || null) : null;

  return {
    institutionId: useDirectInstitution && institutionId ? institutionId : null,
    institution_id: useDirectInstitution && institutionId ? institutionId : null,
    institutionName: directInstitutionName,
    institution_name: directInstitutionName,
    wardId: useDirectInstitution ? wardId : null,
    ward_id: useDirectInstitution ? wardId : null,
    namingConfigId: namingConfigId || null,
    naming_config_id: namingConfigId || null,
    assetCategoryCode: assetCategoryCode || null,
    asset_category_code: assetCategoryCode || null,
    reviewed,
    regenerate: fullRebuild,
    categories: assetCategoryCode ? [assetCategoryCode] : [],
    reviewScope,
    review_status: reviewScope,
    reviewStatus: reviewScope,
    mode: formValues.mode,
    fullRebuild,
    full_rebuild: fullRebuild,
    text_only: Boolean(formValues.textOnly),
  };
};

const hasInstitutionSelector = (formValues) =>
  (formValues.useNamingConfig && Boolean(String(formValues.namingConfigId || "").trim())) ||
  (!formValues.useNamingConfig &&
    Boolean(String(formValues.wardId || "").trim()) &&
    (
      Boolean(String(formValues.institutionId || "").trim()) ||
      Boolean(String(formValues.institutionName || "").trim())
    ));

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** unitIndex;
  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const normalizePdfFile = (file) => {
  if (!file || typeof file !== "object") return null;

  const resourceUrl =
    file.renderUrl ||
    file.render_url ||
    file.downloadUrl ||
    file.download_url ||
    file.url ||
    file.fileUrl ||
    "";
  const fileName = file.fileName || file.file_name || file.filename || file.name || "";
  const fileSizeBytes = Number(file.fileSizeBytes ?? file.size ?? file.file_size ?? 0);

  return {
    ...file,
    fileName,
    renderUrl: file.renderUrl || file.render_url || resourceUrl,
    downloadUrl: file.downloadUrl || file.download_url || resourceUrl,
    resolvedUrl: resourceUrl,
    fileSizeBytes,
    fileSizeLabel: file.fileSizeLabel || file.sizeLabel || (fileSizeBytes > 0 ? formatBytes(fileSizeBytes) : "-"),
    lastModifiedAt:
      file.lastModifiedAt ||
      file.last_modified_at ||
      file.modifiedAt ||
      file.updatedAt ||
      file.last_modified ||
      "-",
  };
};

const normalizePdfGroup = (groupKey, group) => {
  if (!group || typeof group !== "object") return null;

  const rawFiles = Array.isArray(group.files) ? group.files : Array.isArray(group.items) ? group.items : [];
  const files = rawFiles.map(normalizePdfFile).filter(Boolean);
  const totalSizeBytes =
    Number(group.totalSizeBytes ?? group.total_size ?? 0) ||
    files.reduce((sum, file) => sum + (Number(file.fileSizeBytes) || 0), 0);

  return {
    ...group,
    category: group.category || group.categoryName || groupKey,
    categoryName: group.categoryName || group.category_name || group.category || groupKey,
    categoryCode: group.categoryCode || group.category_code || group.code || groupKey?.toUpperCase?.() || "",
    fileCount: Number(group.fileCount ?? group.file_count ?? files.length),
    totalSizeBytes,
    totalSizeLabel: group.totalSizeLabel || group.total_size_label || formatBytes(totalSizeBytes),
    zipDownloadUrl: group.zipDownloadUrl || group.zip_download_url || group.zipUrl || group.zip_url || "",
    files,
  };
};

const normalizePdfGroups = (response) => {
  const payload = parseApiPayload(response);
  if (Array.isArray(payload)) return payload.map((group, index) => normalizePdfGroup(`group-${index}`, group)).filter(Boolean);

  if (payload && typeof payload === "object") {
    return Object.entries(payload)
      .map(([groupKey, value]) => {
        if (Array.isArray(value)) {
          return normalizePdfGroup(groupKey, {
            category: groupKey,
            categoryName: groupKey,
            files: value,
          });
        }

        return normalizePdfGroup(groupKey, value);
      })
      .filter(Boolean);
  }

  return [];
};

const findGroupByCategory = (groups, categoryCode) => {
  const normalized = String(categoryCode || "").trim().toUpperCase();
  if (!normalized) return null;
  return (groups || []).find((group) => {
    const code = String(group?.categoryCode || "").trim().toUpperCase();
    const category = String(group?.category || "").trim().toUpperCase();
    return code === normalized || category === normalized;
  }) || null;
};

const groupFileCount = (group) => Number(group?.fileCount ?? group?.files?.length ?? 0);

const openLink = (url, newTab = true) => {
  if (!url || typeof window === "undefined") return;
  window.open(url, newTab ? "_blank" : "_self", "noopener,noreferrer");
};

const BarcodeDashboard = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [institutions, setInstitutions] = useState([]);
  const [wards, setWards] = useState([]);
  const [institutionSearchInput, setInstitutionSearchInput] = useState("");
  const [assetInstitutionOptions, setAssetInstitutionOptions] = useState([]);
  const [namingConfigs, setNamingConfigs] = useState([]);
  const [isRunningSimple, setIsRunningSimple] = useState(false);
  const [activeRunMode, setActiveRunMode] = useState(null); // 'full' or 'text'
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [generationPreview, setGenerationPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [pdfGroups, setPdfGroups] = useState([]);
  const [loadingPdfGroups, setLoadingPdfGroups] = useState(false);
  const [activeOutputCategory, setActiveOutputCategory] = useState(DEFAULT_FORM.assetCategoryCode);
  const [outputInstitutionNameFilter, setOutputInstitutionNameFilter] = useState("");
  const [pipelineMessage, setPipelineMessage] = useState("");
  const [pipelineOutput, setPipelineOutput] = useState("");
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [barcodeLookupValue, setBarcodeLookupValue] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupExists, setLookupExists] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [shortCodeValue, setShortCodeValue] = useState("");
  const [formError, setFormError] = useState("");
  const [pdfGroupsError, setPdfGroupsError] = useState("");
  const [pdfGroupsInfo, setPdfGroupsInfo] = useState("");
  const [resourceActionLoading, setResourceActionLoading] = useState("");
  const [authDiagnostics, setAuthDiagnostics] = useState({
    hasToken: Boolean(getToken()),
    tokenPreview: getToken() ? `${getToken().slice(0, 18)}...` : "",
    lastStatus: "Not checked",
    lastMessage: "No barcode auth probe has been run yet.",
    checking: false,
  });

  const getExportParams = () => {
    return {
      assetCategoryCode: formValues.assetCategoryCode || "",
      institutionId: formValues.institutionId || "",
      reviewed: formValues.reviewScope === "reviewed" ? "true" : formValues.reviewScope === "unreviewed" ? "false" : "",
      wardId: formValues.wardId || "",
    };
  };

  const fetchPdfGroups = useCallback(async () => {
    setLoadingPdfGroups(true);
    setPdfGroupsError("");
    setPdfGroupsInfo("");
    try {
      const params = {};
      const institutionName = String(outputInstitutionNameFilter || "").trim();
      if (institutionName) {
        params.institutionName = institutionName;
      }
      const pdfResponse = await getBarcodePdfGroups(params);
      setPdfGroups(normalizePdfGroups(pdfResponse));
      setPdfGroupsInfo(pdfResponse?.data?.message || "");
    } catch (error) {
      console.error("Failed to load barcode output groups:", error);
      setPdfGroups([]);
      setPdfGroupsError(
        error?.response?.data?.message ||
          "Barcode output endpoints are not reachable right now. You can still use the page and retry later."
      );
    } finally {
      setLoadingPdfGroups(false);
    }
  }, [outputInstitutionNameFilter]);

  useEffect(() => {
    fetchPdfGroups();
  }, [fetchPdfGroups]);

  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const response = await getAllAstInstitutions();
        setInstitutions(parseListPayload(response));
      } catch (error) {
        console.error("Failed to load institutions:", error);
        setInstitutions([]);
      }
    };

    loadInstitutions();
  }, []);

  useEffect(() => {
    const loadWardsData = async () => {
      try {
        const response = await getAllWards(0, 1000);
        const content = response?.data?.data?.content || response?.data?.content || response?.data?.data || [];
        setWards(content.map(w => ({
          value: w.id,
          label: w.name,
          name: w.name,
        })).sort((a, b) => a.label.localeCompare(b.label)));
      } catch (error) {
        console.error("Failed to load wards:", error);
        setWards([]);
      }
    };
    loadWardsData();
  }, []);

  const loadAssetInstitutionOptions = useCallback(async (searchText, categoryCode, wardIds) => {
    const normalizedSearch = String(searchText || "").trim();
    if (normalizedSearch.length < 2) {
      setAssetInstitutionOptions([]);
      return;
    }

    try {
      const response = await getNamingInstitutionOptions(normalizedSearch, categoryCode, wardIds);
      const rows = response?.data?.data || [];
      setAssetInstitutionOptions(rows.map((row) => {
        const id = String(row.institution_id || row.institutionId || row.id || "").trim();
        const name = row.institution_name || row.institutionName || "";
        const code = String(row.institution_code || row.institutionCode || row.code || "").trim();
        const wardIdVal = row.ward_id || row.wardId || "";
        const wardName = row.ward_name || row.wardName || "";
        const assetCount = row.asset_count || row.assetCount || 0;
        const selectorType = row.selector_type || row.selectorType || (id ? "INSTITUTION_ID" : "UNLINKED_NAME_WARD");
        return {
          code,
          id,
          name,
          value: getInstitutionOptionKey({
            id,
            institution_name: name,
            institution_code: code,
            ward_id: wardIdVal,
          }),
          label: `${name}${wardName ? ` - Ward: ${wardName}` : ""}${code ? ` (${code})` : ""}${assetCount ? ` - ${assetCount} asset(s)` : ""}`,
          selectorType,
          wardId: wardIdVal,
          wardName,
          assetCount,
        };
      }));
    } catch (error) {
      console.error("Failed to load asset-backed institution options:", error);
      setAssetInstitutionOptions([]);
    }
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadAssetInstitutionOptions(institutionSearchInput, formValues.assetCategoryCode, formValues.wardIds);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [formValues.assetCategoryCode, formValues.wardIds, institutionSearchInput, loadAssetInstitutionOptions]);

  useEffect(() => {
    const loadNamingConfigs = async () => {
      try {
        const response = await getNamingConfigSummary();
        const data = response?.data?.data;
        setNamingConfigs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load naming configs:", error);
        setNamingConfigs([]);
      }
    };
    loadNamingConfigs();
  }, []);

  useEffect(() => {
    if (!formError) return;
    setFormError("");
  }, [
    formError,
    formValues.institutionId,
    formValues.institutionCode,
    formValues.institutionName,
    formValues.wardId,
    formValues.namingConfigId,
    formValues.useNamingConfig,
    formValues.assetCategoryCode,
    formValues.reviewScope,
    formValues.mode,
  ]);

  const summary = useMemo(() => {
    const groupCount = pdfGroups.length;
    const totalFiles = pdfGroups.reduce((sum, group) => sum + (group?.fileCount || 0), 0);
    const totalSizeBytes = pdfGroups.reduce((sum, group) => sum + (group?.totalSizeBytes || 0), 0);
    const categoriesWithFiles = new Set(pdfGroups.map((group) => group?.categoryCode).filter(Boolean)).size;

    return [
      { label: "Category Groups", value: groupCount },
      { label: "PDF Files", value: totalFiles },
      { label: "Covered Categories", value: categoriesWithFiles },
      { label: "PDF Size", value: totalSizeBytes ? `${(totalSizeBytes / 1024 / 1024).toFixed(2)} MB` : "0 MB" },
    ];
  }, [pdfGroups]);

  const selectedOutputGroups = useMemo(() => ({
    pdf: findGroupByCategory(pdfGroups, activeOutputCategory),
  }), [activeOutputCategory, pdfGroups]);

  const outputCategoryTabs = useMemo(() => {
    const categoriesWithData = new Set();
    [...pdfGroups].forEach((group) => {
      if (groupFileCount(group) <= 0) return;
      const code = String(group?.categoryCode || "").trim().toUpperCase();
      if (code) {
        categoriesWithData.add(code);
      }
    });

    return BARCODE_CATEGORY_OPTIONS.filter((option) => categoriesWithData.has(option.code));
  }, [pdfGroups]);

  const institutionOptions = useMemo(() => {
    const seen = new Set();
    const typed = institutionSearchInput.trim();
    if (typed.length >= 2 && assetInstitutionOptions.length > 0) {
      return assetInstitutionOptions;
    }

    let filteredList = institutions;
    if (formValues.wardIds && formValues.wardIds.length > 0) {
      filteredList = institutions.filter(inst => {
        const wId = inst?.ward_id || inst?.wardId || "";
        return formValues.wardIds.includes(String(wId).trim());
      });
    }

    return filteredList
      .map((institution, index) => {
        const id = String(institution?.id || institution?.institution_id || institution?.institutionId || "").trim();
        const code = String(institution?.code || "").trim();
        const name = String(institution?.name || "").trim();
        const wardName = String(institution?.ward_name || institution?.wardName || "").trim();
        const wardId = String(institution?.ward_id || institution?.wardId || "").trim();
        const selectorType = String(
          institution?.selector_type ||
            institution?.selectorType ||
            (id ? "INSTITUTION_ID" : "UNLINKED_NAME_WARD")
        ).trim();
        const key = getInstitutionOptionKey(institution, index);
        if (!key || seen.has(key)) return null;
        seen.add(key);
        
        let labelText = name;
        if (wardName && wardName !== "Unknown") {
            labelText += ` - Ward: ${wardName}`;
        }
        if (code) {
            labelText += ` (${code})`;
        }

        return {
          code,
          id,
          name,
          value: key,
          label: labelText || code,
          selectorType,
          wardId,
          wardName,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [assetInstitutionOptions, institutionSearchInput, institutions, formValues.wardIds]);

  const namingConfigOptions = useMemo(() => {
    return namingConfigs.map((nc) => {
      const catLabel = nc.asset_category_code ? `[${nc.asset_category_code}]` : "[All]";
      const instShort = nc.institution_short_name ? ` → ${nc.institution_short_name}` : "";
      const displayName = formatNamingConfigInstitutionLabel(nc);
      return {
        value: nc.id,
        label: `${displayName} ${catLabel}${instShort}`,
        config: nc,
      };
    });
  }, [namingConfigs]);

  const selectedNamingConfigOption = useMemo(() => {
    if (!formValues.namingConfigId) return null;
    return namingConfigOptions.find((o) => o.value === formValues.namingConfigId) || null;
  }, [formValues.namingConfigId, namingConfigOptions]);

  const selectedNamingConfig = selectedNamingConfigOption?.config || null;

  useEffect(() => {
    if (outputCategoryTabs.length === 0) return;
    if (outputCategoryTabs.some((option) => option.code === activeOutputCategory)) return;
    setActiveOutputCategory(outputCategoryTabs[0].code);
  }, [activeOutputCategory, outputCategoryTabs]);

  const validateSimpleGenerate = () => {
    if (!hasInstitutionSelector(formValues)) {
      if (formValues.useNamingConfig) {
        setFormError("Select a naming configuration before running barcode generation.");
      } else {
        setFormError("Select exactly one institution option with a ward before running barcode generation.");
      }
      return false;
    }

    if (!String(formValues.assetCategoryCode || "").trim()) {
      setFormError("Select an asset category before running simple barcode generation.");
      return false;
    }

    setFormError("");
    return true;
  };

  const hasPreviewInputs =
    hasInstitutionSelector(formValues) &&
    Boolean(String(formValues.assetCategoryCode || "").trim());

  const fetchGenerationPreview = useCallback(async () => {
    if (!hasPreviewInputs) {
      setGenerationPreview(null);
      setPreviewError("");
      return;
    }

    setIsLoadingPreview(true);
    setPreviewError("");

    try {
      const response = await previewBarcodeGenerationCount(buildGeneratePayload(formValues));
      setGenerationPreview(normalizeGenerationPreview(parseApiPayload(response)));
    } catch (error) {
      console.error("Failed to load barcode preview:", error);
      setGenerationPreview(null);
      setPreviewError(
        error?.response?.data?.message ||
          error?.message ||
          "Barcode preview is not available right now."
      );
    } finally {
      setIsLoadingPreview(false);
    }
  }, [formValues, hasPreviewInputs]);

  useEffect(() => {
    let isActive = true;

    if (!hasPreviewInputs) {
      setGenerationPreview(null);
      setPreviewError("");
      setIsLoadingPreview(false);
      return undefined;
    }

    setIsLoadingPreview(true);
    setPreviewError("");

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await previewBarcodeGenerationCount(buildGeneratePayload(formValues));
        if (!isActive) return;
        setGenerationPreview(normalizeGenerationPreview(parseApiPayload(response)));
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to load barcode preview:", error);
        setGenerationPreview(null);
        setPreviewError(
          error?.response?.data?.message ||
            error?.message ||
            "Barcode preview is not available right now."
        );
      } finally {
        if (isActive) {
          setIsLoadingPreview(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [formValues, hasPreviewInputs]);

  const handleSimpleGenerate = async (textOnlyOverride = null) => {
    if (!validateSimpleGenerate()) return;

    const isTextOnly = textOnlyOverride !== null ? textOnlyOverride : formValues.textOnly;
    setIsRunningSimple(true);
    setActiveRunMode(isTextOnly ? "text" : "full");
    setPipelineMessage("");
    setPipelineOutput("");
    try {
      const response = await generateBarcodes(buildGeneratePayload({ ...formValues, textOnly: isTextOnly }));

      const status = response?.data?.status;
      const message = response?.data?.message || "Barcode pipeline completed.";
      const payload = parseApiPayload(response);

      setPipelineMessage(message);
      setPipelineOutput(payload?.output || "");

      if (status !== "success" || payload?.success === false) {
        throw new Error(message);
      }

      enqueueSnackbar(message, { variant: "success" });
      fetchPdfGroups();
      fetchGenerationPreview();
    } catch (error) {
      const message = getPipelineErrorMessage(error, "Barcode generation failed.");
      setPipelineMessage(message);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsRunningSimple(false);
      setActiveRunMode(null);
    }
  };

  const handleLookup = async () => {
    if (!barcodeLookupValue.trim()) {
      enqueueSnackbar("Enter a barcode value first.", { variant: "warning" });
      return;
    }

    setIsLookingUp(true);
    setLookupResult(null);
    setLookupExists(null);

    try {
      const [existsResponse, assetResponse] = await Promise.allSettled([
        checkAssetBarcodeExists(barcodeLookupValue.trim()),
        getAssetByBarcode(barcodeLookupValue.trim()),
      ]);
      let hasExistsResult = false;

      if (existsResponse.status === "fulfilled") {
        const existsPayload = parseApiPayload(existsResponse.value);
        setLookupExists(existsPayload);
        hasExistsResult = true;
      }

      if (assetResponse.status === "fulfilled") {
        const assetPayload = parseApiPayload(assetResponse.value);
        setLookupResult(assetPayload);
      } else if (!hasExistsResult) {
        throw assetResponse.reason;
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Barcode lookup failed.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsLookingUp(false);
    }
  };

  const exportButtons = [
    { extension: "csv", label: "Export CSV", icon: "ri-file-text-line" },
    { extension: "xlsx", label: "Export Excel", icon: "ri-file-excel-line" },
  ];

  const refreshAuthDiagnostics = useCallback(async () => {
    const token = getToken();

    setAuthDiagnostics((current) => ({
      ...current,
      hasToken: Boolean(token),
      tokenPreview: token ? `${token.slice(0, 18)}...` : "",
      checking: true,
    }));

    try {
      await getBarcodePdfGroups();
      setAuthDiagnostics({
        hasToken: Boolean(token),
        tokenPreview: token ? `${token.slice(0, 18)}...` : "",
        lastStatus: "Authorized",
        lastMessage: "Barcode PDF endpoint accepted the current session token.",
        checking: false,
      });
    } catch (error) {
      const statusCode = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Barcode auth probe failed.";

      setAuthDiagnostics({
        hasToken: Boolean(token),
        tokenPreview: token ? `${token.slice(0, 18)}...` : "",
        lastStatus: statusCode ? `HTTP ${statusCode}` : "Network Error",
        lastMessage: message,
        checking: false,
      });
    }
  }, []);

  useEffect(() => {
    refreshAuthDiagnostics();
  }, [refreshAuthDiagnostics]);

  const withResourceAction = async (key, action) => {
    setResourceActionLoading(key);
    try {
      await action();
    } catch (error) {
      const message =
        error?.message ||
        "The requested barcode resource could not be opened.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setResourceActionLoading("");
    }
  };

  const handleProtectedDownload = (url, fallbackName, actionKey) =>
    withResourceAction(actionKey, () => downloadBarcodeProtectedResource(url, fallbackName));

  const handleProtectedOpen = (url, actionKey) =>
    withResourceAction(actionKey, () => openBarcodeProtectedResource(url));

  const handlePreviewPdf = async (url, categoryName, fileName) => {
    await withResourceAction(`preview:${fileName}`, async () => {
      const { blob } = await fetchBarcodeBlob(url);
      const objectUrl = URL.createObjectURL(blob);
      setSelectedPdf((current) => {
        if (current?.objectUrl) {
          URL.revokeObjectURL(current.objectUrl);
        }
        return {
          categoryName,
          fileName,
          renderUrl: objectUrl,
          objectUrl,
        };
      });
    });
  };


  const closePreview = () => {
    setSelectedPdf((current) => {
      if (current?.objectUrl) {
        URL.revokeObjectURL(current.objectUrl);
      }
      return null;
    });
  };

  useEffect(() => {
    closePreview();
  }, [activeOutputCategory, outputInstitutionNameFilter]);

  return (
    <React.Fragment>
      <div className="page-content barcode-dashboard">
        <Container fluid>
          <Breadcrumbs title="Barcode Module" breadcrumbItem="Barcode Operations" />

          <Row className="g-3 mb-4">
            {summary.map((item) => (
              <Col xl={summary.length > 4 ? 2 : 3} md={6} key={item.label}>
                <div className="barcode-summary-card">
                  <div className="text-muted text-uppercase small fw-semibold mb-2">{item.label}</div>
                  <div className="barcode-summary-value">{item.value}</div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="mb-4">
            <Col xl={12}>
              <Card className="barcode-card">
                <CardBody className="py-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                      <div className="fw-semibold mb-2">Auth Diagnostics</div>
                      <div className="small text-muted mb-1">
                        Token present:{" "}
                        <span className={authDiagnostics.hasToken ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                          {authDiagnostics.hasToken ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className="small text-muted mb-1">
                        Token preview: <code>{authDiagnostics.tokenPreview || "No token in localStorage"}</code>
                      </div>
                      <div className="small text-muted mb-1">
                        Barcode endpoint status: <strong>{authDiagnostics.lastStatus}</strong>
                      </div>
                      <div className="small text-muted">
                        {authDiagnostics.lastMessage}
                      </div>
                    </div>
                    <Button
                      type="button"
                      color="light"
                      onClick={refreshAuthDiagnostics}
                      disabled={authDiagnostics.checking}
                    >
                      {authDiagnostics.checking ? <Spinner size="sm" className="me-2" /> : <i className="ri-shield-check-line me-2" />}
                      Recheck Auth
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="g-4">
            <Col xl={5}>
              <Card className="barcode-card h-100">
                <CardHeader className="bg-white border-0 pb-0 d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-1">Generate Barcodes</h4>
                    <p className="text-muted mb-0">Preview the expected count, then generate barcode data and PDF previews.</p>
                  </div>
                  <Button color="link" className="p-0 text-decoration-none" onClick={() => navigate("/barcode-naming-config")}>
                    <i className="ri-settings-4-line me-1"></i> Naming Rules
                  </Button>
                </CardHeader>
                <CardBody>
                  <Form>
                    <Row className="g-3 mb-3">
                      {/* Naming Config toggle */}
                      <Col md={12}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Switch
                            id="useNamingConfigToggle"
                            checked={Boolean(formValues.useNamingConfig)}
                            onChange={(checked) =>
                              setFormValues((cur) => ({
                                ...cur,
                                useNamingConfig: checked,
                                institutionId: "",
                                institutionCode: "",
                                institutionName: "",
                                institutionSelectorType: "",
                                wardId: "",
                                namingConfigId: "",
                              }))
                            }
                            uncheckedIcon={false}
                            checkedIcon={false}
                            onColor="#4b38b3"
                            offColor="#adb5bd"
                            onHandleColor="#ffffff"
                            offHandleColor="#ffffff"
                            height={20}
                            width={42}
                          />
                          <Label for="useNamingConfigToggle" className="mb-0 fw-semibold">
                            Use Naming Config
                          </Label>
                        </div>
                        <div className="text-muted small">
                          {formValues.useNamingConfig
                            ? "Pick a saved naming config — the system will use its custom institution/location codes."
                            : "Toggle on to generate barcodes using a saved naming configuration's codes."}
                        </div>
                      </Col>

                      {formValues.useNamingConfig ? (
                        /* Naming config selector */
                        <Col md={12}>
                          <Label className="form-label">Naming Configuration</Label>
                          <Select
                            options={namingConfigOptions}
                            value={selectedNamingConfigOption}
                            onChange={(opt) => {
                              if (opt) {
                                setFormValues((cur) => ({
                                  ...cur,
                                  namingConfigId: opt.value,
                                  institutionName: opt.config?.institution_name || "",
                                  institutionId: "",
                                  institutionCode: "",
                                  institutionSelectorType: "",
                                  wardId: "",
                                }));
                              } else {
                                setFormValues((cur) => ({
                                  ...cur,
                                  namingConfigId: "",
                                  institutionId: "",
                                  institutionName: "",
                                  institutionCode: "",
                                  institutionSelectorType: "",
                                  wardId: "",
                                }));
                              }
                            }}
                            isClearable
                            isSearchable
                            placeholder="Search naming configurations..."
                          />
                          {selectedNamingConfig && (
                            <div className="mt-2 p-2 border rounded bg-light small">
                              <div className="fw-semibold mb-1">Will apply codes:</div>
                              <div className="d-flex flex-wrap gap-3">
                                {selectedNamingConfig.location_short_name && (
                                  <span>
                                    Location: <code className="text-primary">{selectedNamingConfig.location_short_name}</code>
                                  </span>
                                )}
                                {selectedNamingConfig.institution_short_name && (
                                  <span>
                                    Institution: <code className="text-success">{selectedNamingConfig.institution_short_name}</code>
                                  </span>
                                )}
                                {selectedNamingConfig.asset_short_name && (
                                  <span>
                                    Asset: <code className="text-info">{selectedNamingConfig.asset_short_name}</code>
                                  </span>
                                )}
                              </div>
                              <div className="text-muted mt-1">
                                Institution: <strong>
                                  {formatNamingConfigInstitutionLabel(selectedNamingConfig)}
                                </strong>
                                {selectedNamingConfig.asset_category_code && (
                                  <span className="ms-2">· Category: <strong>{selectedNamingConfig.asset_category_code}</strong></span>
                                )}
                              </div>
                            </div>
                          )}
                        </Col>
                      ) : (
                        /* Standard institution selector */
                        <>
                          <Col md={12}>
                            <Label className="form-label">Ward</Label>
                            <Select
                              isMulti
                              options={wards}
                              value={
                                formValues.wardIds
                                  ? wards.filter((item) => formValues.wardIds.includes(String(item.value)))
                                  : []
                              }
                              onChange={(selected) => {
                                setFormValues({
                                  ...formValues,
                                  wardIds: selected ? selected.map(s => s.value) : [],
                                  institutionId: "",
                                  institutionCode: "",
                                  institutionName: "",
                                  institutionSelectorType: "",
                                  wardId: "",
                                });
                                setInstitutionSearchInput("");
                              }}
                              isClearable
                              isSearchable
                              placeholder="Select Ward..."
                            />
                          </Col>
                          <Col md={12}>
                            <Label className="form-label">Institution</Label>
                            <Select
                              options={institutionOptions}
                              value={
                                formValues.institutionId || formValues.institutionName
                                  ? institutionOptions.find((item) =>
                                      (formValues.institutionId && item.id === formValues.institutionId) ||
                                      (
                                        item.name === formValues.institutionName &&
                                        String(item.wardId || "") === String(formValues.wardId || formValues.wardIds?.[0] || "")
                                      )
                                    ) || {
                                      value: formValues.institutionId || formValues.institutionName,
                                      label: formValues.institutionName,
                                      id: formValues.institutionId || "",
                                      name: formValues.institutionName,
                                      wardId: formValues.wardId || formValues.wardIds?.[0] || "",
                                      code: formValues.institutionCode || "",
                                      selectorType: formValues.institutionSelectorType || "",
                                    }
                                  : null
                              }
                              inputValue={institutionSearchInput}
                              onInputChange={(value, actionMeta) => {
                                if (actionMeta.action === "input-change") {
                                  setInstitutionSearchInput(value);
                                }
                                if (actionMeta.action === "menu-close") {
                                  setInstitutionSearchInput("");
                                }
                              }}
                              onChange={(selected) => {
                                if (selected) {
                                  setFormValues((current) => ({
                                    ...current,
                                    institutionId: selected.id || "",
                                    institutionCode: selected.code || "",
                                    institutionName: selected.name,
                                    institutionSelectorType:
                                      selected.selectorType || (selected.id ? "INSTITUTION_ID" : "UNLINKED_NAME_WARD"),
                                    wardId: selected.wardId || "",
                                    wardIds: selected.wardId ? [selected.wardId] : [],
                                  }));
                                  setInstitutionSearchInput("");
                                } else {
                                  setFormValues((current) => ({
                                    ...current,
                                    institutionId: "",
                                    institutionCode: "",
                                    institutionName: "",
                                    institutionSelectorType: "",
                                    wardId: "",
                                    wardIds: [],
                                  }));
                                  setInstitutionSearchInput("");
                                }
                              }}
                              isClearable
                              isSearchable
                              placeholder="Search and select institution..."
                            />
                            <div className="text-muted small mt-2">
                              Select the exact institution whose barcode data you want to generate.
                            </div>
                          </Col>
                        </>
                      )}
                      <Col md={12}>
                        <Label className="form-label">Asset Category</Label>
                        <Input
                          type="select"
                          value={formValues.assetCategoryCode}
                          onChange={(event) => {
                            const nextCategory = event.target.value;
                            setFormValues((current) => ({
                              ...current,
                              assetCategoryCode: nextCategory,
                            }));
                            setActiveOutputCategory(nextCategory);
                          }}
                        >
                          {BARCODE_CATEGORY_OPTIONS.map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label} ({option.code})
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </Row>

                    <Row className="g-3">
                      <Col md={6}>
                        <Label className="form-label">Review Scope</Label>
                        <Input
                          type="select"
                          value={formValues.reviewScope}
                          onChange={(event) =>
                            setFormValues((current) => ({ ...current, reviewScope: event.target.value }))
                          }
                        >
                          {BARCODE_REVIEW_SCOPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Mode</Label>
                        <Input
                          type="select"
                          value={formValues.mode}
                          onChange={(event) =>
                            setFormValues((current) => ({ ...current, mode: event.target.value }))
                          }
                        >
                          <option value="new">Generate Missing</option>
                          <option value="all">Full Rebuild</option>
                        </Input>
                      </Col>
                    </Row>
                    {hasPreviewInputs ? (
                      <Alert color={previewError ? "warning" : "secondary"} className="mt-3 mb-0">
                        {isLoadingPreview ? (
                          <span><Spinner size="sm" className="me-2" />Loading barcode preview...</span>
                        ) : previewError ? (
                          previewError
                        ) : generationPreview ? (
                          <div>
                            <div className="d-flex flex-wrap gap-3 mb-2">
                              <span><strong>{generationPreview.barcodesToGenerate ?? 0}</strong> barcodes to generate</span>
                              {generationPreview.assetCategoryCode === "BLDG" && (
                                <>
                                  <span className="text-primary"><strong>{generationPreview.buildingCount ?? 0}</strong> buildings</span>
                                  <span className="text-success"><strong>{generationPreview.siteWorksCount ?? 0}</strong> site works</span>
                                </>
                              )}
                              <span><strong>{generationPreview.matchingAssets ?? 0}</strong> matching assets</span>
                              <span><strong>{generationPreview.existingLabels ?? 0}</strong> existing labels</span>
                              <span><strong>{generationPreview.totalRequiredLabels ?? 0}</strong> total required labels</span>
                            </div>
                            {generationPreview.hasExistingBarcodes && (
                              <div className="text-danger fw-semibold mb-2">
                                <i className="ri-error-warning-line me-1" />
                                This selection already has generated barcodes! ({generationPreview.existingLabels} existing)
                              </div>
                            )}
                            {generationPreview.matchedInstitutions && generationPreview.matchedInstitutions.length > 0 && (
                              <div className="small text-muted border-top pt-2">
                                <strong>Matched spellings / combined institutions in this batch:</strong>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {generationPreview.matchedInstitutions.map((inst, i) => (
                                    <Badge key={i} color="light" className="border text-dark">{inst}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          "No preview data is available for the current selection."
                        )}
                      </Alert>
                    ) : null}
                    {formError ? <Alert color="danger" className="mt-3 mb-0 py-2">{formError}</Alert> : null}

                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Button
                        type="button"
                        color="primary"
                        onClick={() => handleSimpleGenerate(false)}
                        disabled={isRunningSimple}
                        title="Generate barcodes and create PDF files"
                      >
                        {isRunningSimple && activeRunMode === "full" ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <i className="ri-play-line me-2" />
                        )}
                        Run Generate
                      </Button>
                      <Button
                        type="button"
                        color="info"
                        outline
                        onClick={() => handleSimpleGenerate(true)}
                        disabled={isRunningSimple}
                        title="Update barcode text only (Bypasses PDF generation for speed)"
                      >
                        {isRunningSimple && activeRunMode === "text" ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <i className="ri-speed-up-line me-2" />
                        )}
                        Run Text-Only
                      </Button>
                      <Button
                        type="button"
                        color="success"
                        outline
                        disabled={Boolean(resourceActionLoading)}
                        onClick={() =>
                          handleProtectedDownload(
                            getBarcodeExportUrl("xlsx", getExportParams()),
                            `barcode-export.xlsx`,
                            `export:xlsx`
                          )
                        }
                        title="Export all barcodes with details to Excel"
                      >
                        {resourceActionLoading === "export:xlsx" ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <i className="ri-file-excel-line me-2" />
                        )}
                        Export Excel
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        outline
                        disabled={Boolean(resourceActionLoading)}
                        onClick={() =>
                          handleProtectedDownload(
                            getBarcodeExportUrl("csv", getExportParams()),
                            `barcode-export.csv`,
                            `export:csv`
                          )
                        }
                        title="Export all barcodes to CSV"
                      >
                        {resourceActionLoading === "export:csv" ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <i className="ri-file-text-line me-2" />
                        )}
                        Export CSV
                      </Button>
                      <Button type="button" color="light" onClick={fetchPdfGroups} disabled={loadingPdfGroups}>
                        <i className="ri-refresh-line me-2" />
                        Refresh Outputs
                      </Button>
                    </div>
                    {selectedOutputGroups.pdf ? (
                      <Alert color="light" className="border mt-3 mb-0">
                        <div className="fw-semibold mb-2">Current category outputs</div>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedOutputGroups.pdf?.zipDownloadUrl ? (
                            <Button
                              type="button"
                              color="primary"
                              outline
                              size="sm"
                              disabled={Boolean(resourceActionLoading)}
                              onClick={() =>
                                handleProtectedDownload(
                                  resolveBarcodeUrl(selectedOutputGroups.pdf.zipDownloadUrl),
                                  `${selectedOutputGroups.pdf.category || selectedOutputGroups.pdf.categoryCode || "barcode-pdfs"}.zip`,
                                  `selected-pdf:${selectedOutputGroups.pdf.category || selectedOutputGroups.pdf.categoryCode}`
                                )
                              }
                            >
                              <i className="ri-file-pdf-2-line me-2" />
                              Download PDF ZIP
                            </Button>
                          ) : null}
                        </div>
                      </Alert>
                    ) : null}
                  </Form>
                </CardBody>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="barcode-card h-100">
                <CardHeader className="bg-white border-0 pb-0 d-flex justify-content-between align-items-start">
                  <div>
                    <h4 className="card-title mb-1">Pipeline Output</h4>
                    <p className="text-muted mb-0">Review backend execution logs after each barcode operation.</p>
                  </div>
                  <div className="d-flex gap-2">
                    {exportButtons.map((item) => (
                      <Button
                        type="button"
                        key={item.extension}
                        color="light"
                        disabled={Boolean(resourceActionLoading)}
                        onClick={() =>
                          handleProtectedDownload(
                            getBarcodeExportUrl(item.extension, getExportParams()),
                            `barcode-export.${item.extension}`,
                            `export:${item.extension}`
                          )
                        }
                      >
                        {resourceActionLoading === `export:${item.extension}` ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <i className={`${item.icon} me-2`} />
                        )}
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </CardHeader>
                <CardBody>
                  {pipelineMessage ? (
                    <Alert color="info" className="mb-3">
                      {pipelineMessage}
                    </Alert>
                  ) : null}
                  <div className="barcode-output">
                    {pipelineOutput || "Run a barcode operation to view backend script output here."}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="g-4 mt-1">
            <Col xl={4}>
              <Card className="barcode-card h-100">
                <CardHeader className="bg-white border-0 pb-0">
                  <h4 className="card-title mb-1">Lookup & Scan</h4>
                  <p className="text-muted mb-0">Check barcode existence, inspect linked assets, and open scan pages.</p>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label">Barcode Value</Label>
                    <Input
                      value={barcodeLookupValue}
                      onChange={(event) => setBarcodeLookupValue(event.target.value)}
                      placeholder="Enter full barcode"
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button type="button" color="primary" onClick={handleLookup} disabled={isLookingUp}>
                      {isLookingUp ? <Spinner size="sm" className="me-2" /> : <i className="ri-search-line me-2" />}
                      Lookup
                    </Button>
                    <Button
                      type="button"
                      color="light"
                      disabled={!barcodeLookupValue.trim() || Boolean(resourceActionLoading)}
                      onClick={() =>
                        handleProtectedOpen(
                          getBarcodeScanUrl(barcodeLookupValue.trim()),
                          "scan:path"
                        )
                      }
                    >
                      <i className="ri-qr-scan-line me-2" />
                      Open Scan Path
                    </Button>
                    <Button
                      type="button"
                      color="light"
                      disabled={!barcodeLookupValue.trim() || Boolean(resourceActionLoading)}
                      onClick={() =>
                        handleProtectedOpen(
                          getBarcodeQueryScanUrl(barcodeLookupValue.trim()),
                          "scan:query"
                        )
                      }
                    >
                      <i className="ri-link me-2" />
                      Open Query Scan
                    </Button>
                  </div>

                  {lookupExists !== null ? (
                    <Alert color="secondary">
                      <strong>Exists check:</strong>{" "}
                      <span>{typeof lookupExists === "object" ? JSON.stringify(lookupExists) : String(lookupExists)}</span>
                    </Alert>
                  ) : null}

                  {lookupResult ? (
                    <div className="border rounded-3 p-3 bg-light-subtle">
                      <div className="fw-semibold mb-2">Asset Result</div>
                      <div className="small text-muted mb-2">
                        Response payload from <code>/assets/by-barcode/&lt;barcode&gt;</code>
                      </div>
                      <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(lookupResult, null, 2)}
                      </pre>
                    </div>
                  ) : null}

                  <hr className="my-4" />

                  <div className="mb-3">
                    <Label className="form-label">Short Token or URL</Label>
                    <Input
                      value={shortCodeValue}
                      onChange={(event) => setShortCodeValue(event.target.value)}
                      placeholder="AM6AG2E4XK or full /s/ URL"
                    />
                  </div>
                  <Button type="button" color="dark" outline onClick={() => openLink(getBarcodeShortUrl(shortCodeValue.trim()))}>
                    <i className="ri-external-link-line me-2" />
                    Open Public Short URL
                  </Button>
                </CardBody>
              </Card>
            </Col>

            <Col xl={8}>
              <Card className="barcode-card">
                <CardHeader className="bg-white border-0 pb-0 d-flex justify-content-between align-items-start">
                  <div>
                    <h4 className="card-title mb-1">Barcode Outputs</h4>
                    <p className="text-muted mb-0">Use category tabs to view PDF previews.</p>
                  </div>
                  {loadingPdfGroups ? <Spinner size="sm" /> : null}
                </CardHeader>
                <CardBody>
                  <Row className="g-3 align-items-end mb-3">
                    <Col md={7}>
                      <Label className="form-label">Filter output files by institution</Label>
                      <Select
                        options={institutionOptions}
                        value={
                          outputInstitutionNameFilter
                            ? institutionOptions.find((item) => item.value === outputInstitutionNameFilter) || {
                                value: outputInstitutionNameFilter,
                                label: outputInstitutionNameFilter,
                              }
                            : null
                        }
                        onChange={(selectedOption) => setOutputInstitutionNameFilter(selectedOption?.value || "")}
                        isClearable
                        isSearchable
                        placeholder="All institutions"
                      />
                    </Col>
                    <Col md={5}>
                      <Button type="button" color="light" onClick={fetchPdfGroups} disabled={loadingPdfGroups}>
                        <i className="ri-refresh-line me-2" />
                        Apply Filter
                      </Button>
                    </Col>
                  </Row>

                  <div className="barcode-category-tabs mb-3">
                    {outputCategoryTabs.map((option) => (
                      <button
                        type="button"
                        key={option.code}
                        className={`barcode-category-tab ${activeOutputCategory === option.code ? "is-active" : ""}`}
                        onClick={() => setActiveOutputCategory(option.code)}
                      >
                        <span>{option.label}</span>
                        <Badge color="light" className="text-muted border">{option.code}</Badge>
                      </button>
                    ))}
                  </div>
                  {outputCategoryTabs.length === 0 && !loadingPdfGroups ? (
                    <Alert color="light" className="border mb-3">
                      No barcode PDF files match the selected institution filter.
                    </Alert>
                  ) : null}

                  {pdfGroupsInfo ? (
                    <Alert color="info" className="mb-3">
                      {pdfGroupsInfo}
                    </Alert>
                  ) : null}
                  {pdfGroupsError ? (
                    <Alert color="warning" className="mb-3">
                      {pdfGroupsError}
                    </Alert>
                  ) : null}

                  <Row className="g-3">
                    <Col lg={12}>
                      <div className="barcode-preview-panel">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                          <div>
                            <h5 className="mb-1">PDF Preview</h5>
                            <div className="text-muted small">
                              {selectedOutputGroups.pdf?.fileCount || 0} files
                              {selectedOutputGroups.pdf?.totalSizeLabel ? ` • ${selectedOutputGroups.pdf.totalSizeLabel}` : ""}
                            </div>
                          </div>
                          <Button
                            type="button"
                            color="primary"
                            outline
                            size="sm"
                            disabled={Boolean(resourceActionLoading) || !selectedOutputGroups.pdf?.zipDownloadUrl}
                            onClick={() =>
                              handleProtectedDownload(
                                resolveBarcodeUrl(selectedOutputGroups.pdf.zipDownloadUrl),
                                `${selectedOutputGroups.pdf.category || selectedOutputGroups.pdf.categoryCode || "barcode-pdfs"}.zip`,
                                `zip:${selectedOutputGroups.pdf.category || selectedOutputGroups.pdf.categoryCode}`
                              )
                            }
                          >
                            PDF ZIP
                          </Button>
                        </div>

                        {!selectedOutputGroups.pdf && !loadingPdfGroups ? (
                          <Alert color="light" className="border">No PDF files for this category.</Alert>
                        ) : null}

                        <div className="barcode-file-list">
                          {(selectedOutputGroups.pdf?.files || []).map((file) => (
                            <div className="barcode-file-row py-2" key={file.fileName}>
                              <div className="fw-semibold text-truncate">{file.fileName || "-"}</div>
                              <div className="small text-muted mb-2">{file.fileSizeLabel || "-"} • {file.lastModifiedAt || "-"}</div>
                              <div className="d-flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  color="light"
                                  disabled={Boolean(resourceActionLoading) || !file.renderUrl}
                                  onClick={() => handlePreviewPdf(resolveBarcodeUrl(file.renderUrl), selectedOutputGroups.pdf.categoryName, file.fileName)}
                                >
                                  {resourceActionLoading === `preview:${file.fileName}` ? <Spinner size="sm" /> : "Preview"}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="light"
                                  disabled={Boolean(resourceActionLoading) || !file.renderUrl}
                                  onClick={() => handleProtectedOpen(resolveBarcodeUrl(file.renderUrl), `open:${file.fileName}`)}
                                >
                                  Open
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="primary"
                                  outline
                                  disabled={Boolean(resourceActionLoading) || !file.downloadUrl}
                                  onClick={() =>
                                    handleProtectedDownload(resolveBarcodeUrl(file.downloadUrl), file.fileName, `download:${file.fileName}`)
                                  }
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {selectedPdf ? (
                          <div className="mt-3">
                            <div className="small text-muted mb-2">{selectedPdf.fileName}</div>
                            <iframe title={selectedPdf.fileName} className="barcode-inline-preview-frame" src={selectedPdf.renderUrl} />
                          </div>
                        ) : null}
                      </div>
                    </Col>


                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={Boolean(selectedPdf)} toggle={closePreview} size="xl">
        <ModalHeader toggle={closePreview}>
          {selectedPdf ? `${selectedPdf.categoryName || "Barcode PDF"} • ${selectedPdf.fileName}` : "Barcode PDF"}
        </ModalHeader>
        <ModalBody>
          {selectedPdf ? (
            <>
              <div className="small text-muted mb-3">
                URL used: <code>{selectedPdf.renderUrl}</code>
              </div>
              <iframe
                title={selectedPdf.fileName}
                className="barcode-preview-frame"
                src={selectedPdf.renderUrl}
              />
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default BarcodeDashboard;
